import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiUsers, FiBook, FiMapPin, FiDollarSign, FiCreditCard, FiCheck, FiCheckCircle, FiExternalLink } = FiIcons;

const Donate = () => {
  const [donationType, setDonationType] = useState('monthly');
  const [amount, setAmount] = useState(50);
  const [isDonated, setIsDonated] = useState(false);

  const handleDonate = (e) => {
    e.preventDefault();
    // Redirect to external giving platform
    window.open('https://giving.myamplify.io/app/giving/clodo-youth180inc', '_blank');
  };

  const impactAreas = [
    {
      icon: FiUsers,
      title: 'Campus Outreach Teams',
      description: 'Support volunteer teams reaching students on college campuses across Florida.',
      cost: '$100/month funds one campus team'
    },
    {
      icon: FiMapPin,
      title: 'City Evangelism',
      description: 'Enable street outreach, prayer walks, and worship nights in urban areas.',
      cost: '$75/month covers city outreach supplies'
    },
    {
      icon: FiBook,
      title: 'Discipleship Resources',
      description: 'Provide Bibles, study materials, and resources for new believers.',
      cost: '$25 provides resources for 5 students'
    },
    {
      icon: FiHeart,
      title: 'Ministry Operations',
      description: 'Support administrative costs, training, and ministry coordination.',
      cost: '$50/month helps with general operations'
    }
  ];

  const testimonials = [
    {
      name: 'Pastor John Smith',
      church: 'Grace Community Church',
      quote: 'Supporting Love 180 has been one of the best investments our church has made. We\'ve seen incredible fruit from their work on local campuses.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      name: 'Sarah & Mike Johnson',
      role: 'Monthly Donors',
      quote: 'As parents of college students, we know how important it is to have ministries like Love 180 on campuses. Our monthly giving helps reach the next generation.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    }
  ];

  const givingUrl = 'https://giving.myamplify.io/app/giving/clodo-youth180inc';

  return (
    <div>
      <Hero
        title="Support the Mission"
        description="Your partnership enables us to reach more students and young adults across Florida with the life-transforming message of Jesus Christ. Every gift makes an eternal difference."
        primaryCta={{
          text: 'Give Now',
          onClick: () => window.open(givingUrl, '_blank')
        }}
        backgroundImage="https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Impact Areas */}
      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Your Impact
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            See how your donation directly supports our mission to turn lives toward Jesus 180 degrees at a time.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {impactAreas.map((area, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={area.icon} className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{area.title}</h3>
                    <p className="text-neutral-600 mb-3">{area.description}</p>
                    <div className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full inline-block">
                      {area.cost}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Donation CTA Section */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center bg-gradient-to-br from-primary-600 to-primary-800 text-white shadow-2xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SafeIcon icon={FiHeart} className="w-20 h-20 mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Make Your Gift Today</h2>
              <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
                Your generosity enables us to reach more students across Florida campuses and cities. Click below to give securely through our trusted giving platform.
              </p>
              <button
                onClick={() => window.open(givingUrl, '_blank')}
                className="inline-flex items-center justify-center bg-white text-primary-600 px-10 py-5 rounded-xl text-xl font-bold hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <SafeIcon icon={FiExternalLink} className="w-6 h-6 mr-3" />
                Give Securely Online
              </button>
              <p className="text-sm text-primary-200 mt-6">
                Powered by Amplify - Secure & Tax-Deductible
              </p>
            </motion.div>
          </Card>
        </div>
      </Section>

      {/* Alternative Giving Options */}
      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Other Ways to Give
          </h2>
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
            We understand that everyone's giving situation is different. Here are additional ways to support Love 180 ministry.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-6 text-center">
            <SafeIcon icon={FiCheck} className="w-8 h-8 text-primary-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-3">Check Donations</h3>
            <p className="text-neutral-600 mb-4">
              Mail checks made payable to "Youth 180 Inc" to our office address.
            </p>
            <div className="text-sm text-neutral-500 bg-neutral-50 p-4 rounded-lg">
              Youth 180 Inc<br />
              PO Box 12345<br />
              Gainesville, FL 32601
            </div>
          </Card>

          <Card className="p-6 text-center">
            <SafeIcon icon={FiUsers} className="w-8 h-8 text-primary-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-3">Corporate Sponsorship</h3>
            <p className="text-neutral-600 mb-4">
              Partner with us as a business to sponsor events or ongoing ministry activities.
            </p>
            <a href="/contact">
              <Button variant="outline" size="sm">
                Contact Us
              </Button>
            </a>
          </Card>

          <Card className="p-6 text-center">
            <SafeIcon icon={FiHeart} className="w-8 h-8 text-primary-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-3">In-Kind Donations</h3>
            <p className="text-neutral-600 mb-4">
              Donate supplies, equipment, or services that support our outreach efforts.
            </p>
            <a href="/contact">
              <Button variant="outline" size="sm">
                Learn More
              </Button>
            </a>
          </Card>
        </div>
      </Section>

      {/* Donor Testimonials */}
      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            From Our Partners
          </h2>
          <p className="text-lg text-neutral-600">
            Hear from churches and individuals who support Love 180 ministry.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6">
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-neutral-900">{testimonial.name}</h4>
                    <p className="text-sm text-neutral-600">{testimonial.church || testimonial.role}</p>
                  </div>
                </div>
                <p className="text-neutral-700 italic">"{testimonial.quote}"</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Tax Information */}
      <Section>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">
            Tax-Deductible Giving
          </h2>
          <p className="text-lg text-neutral-600 mb-6">
            Youth 180 Inc (Love 180 Ministry) is a 501(c)(3) non-profit organization. All donations are tax-deductible to the full extent allowed by law. You will receive a donation receipt for your records.
          </p>
          
          <div className="bg-neutral-50 p-6 rounded-lg">
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Tax ID Number</h4>
                <p className="text-neutral-600">Available upon request</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Receipts</h4>
                <p className="text-neutral-600">Emailed immediately after donation</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Annual Statements</h4>
                <p className="text-neutral-600">Sent by January 31st each year</p>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Donate;